/**
 * src/components/agent/ChatComposerV2.jsx — Unified chat composer.
 *
 * Phase 82-04 — Chat System Unification (wave 3).
 *
 * Replaces the legacy ChatInputBar mount inside AgentShell. Owns:
 *   - Auto-growing textarea (44 → 200px, scroll after)
 *   - 4000-character cap via textarea maxLength + onChange slice (truncates,
 *     never rejects a paste — see TICKET-CHAT-PASTE-SOFTFAIL)
 *   - Draft persistence (localStorage acn_chat_draft_<id|new>)
 *   - Slash command menu (useSlashCommands + SlashCommandMenu)
 *   - @-mention autocomplete (useAtMentions + AtMentionPicker)
 *   - Attach menu + send/stop button
 *   - Ctrl/Cmd+K opens slash menu manually
 *
 * Tool-dispatch commands are sent as plain text; the agent loop resolves
 * toolName at its end. Local-dispatch commands call onLocalCommand(handler).
 *
 * Hooks at top of body — Phase 90-07 rule.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Send, StopCircle, Plus, Mic, Headphones, Loader2 } from 'lucide-react';
import SlashCommandMenu from './SlashCommandMenu';
import AtMentionPicker from './AtMentionPicker';
import AttachMenu from './AttachMenu';
import DictationWaveform from './DictationWaveform';
import DictationRecoveryBar from '../shared/DictationRecoveryBar.jsx';
import DictationRestoreBar from './DictationRestoreBar.jsx';
import useDictation from '../../hooks/useDictation';
import useDictationSafetyNet from '../../hooks/useDictationSafetyNet';
import { useSlashCommands } from '../../lib/hooks/useSlashCommands';
import { useAtMentions } from '../../lib/hooks/useAtMentions';
import { validate as validateCommand, COMMANDS } from '../../lib/agent/slashCommandRegistry';
import {
  composerRootStyle,
  composerRowStyle,
  textareaWrapStyle,
  textareaStyle,
  sendBtnStyle,
  sendBtnDisabledStyle,
  iconBtnStyle,
  iconBtnActiveStyle,
  metaRowStyle,
  counterStyle,
  counterWarnStyle,
  counterErrorStyle,
} from './ChatComposerV2.styles';

// Generous cap so a long, offline-written message pastes in full. Claude handles
// far more, chat_messages.content is unlimited `text`, and the server doesn't
// reject on length — 4000 was an arbitrary UI limit that silently ate long
// pastes (TICKET-CHAT-PASTE-SOFTFAIL). Trimming past this is now WARNED, not silent.
const MAX_CHARS = 16000;
const WARN_AT = 15200;
const GROW_MIN = 44;
const GROW_MAX = 200;

const DRAFT_PREFIX = 'acn_chat_draft_';

function draftKey(conversationId) {
  return `${DRAFT_PREFIX}${conversationId ?? 'new'}`;
}

function loadDraft(cid) {
  if (typeof window === 'undefined') return '';
  try { return window.localStorage.getItem(draftKey(cid)) || ''; }
  catch { return ''; }
}

function saveDraft(cid, value) {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.localStorage.setItem(draftKey(cid), value);
    else window.localStorage.removeItem(draftKey(cid));
  } catch { /* storage blocked */ }
}

export default function ChatComposerV2({
  chat,
  conversationId = null,
  onSend,
  onStop,
  onVoiceMode,
  onLocalCommand,
  onFileSelect,
  voiceMode = false,
}) {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [caret, setCaret] = useState(0);
  const [manualSlashOpen, setManualSlashOpen] = useState(false);

  const input = chat?.input ?? '';
  const setInput = chat?.setInput ?? (() => {});
  const loading = Boolean(chat?.loading);

  // One-click dictation: mic button starts recording, shows live waveform
  // where the icon used to be, second click stops + transcribes + inserts
  // the text into the composer. No chunks, no pipeline — single blob → one
  // Whisper call → text. See src/hooks/useDictation.js.
  const appendText = (text) => {
    if (!text) return;
    const glue = input && !/\s$/.test(input) ? ' ' : '';
    const next = (input + glue + text).slice(0, MAX_CHARS);
    setInput(next);
    queueMicrotask(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        const pos = next.length;
        try { el.setSelectionRange(pos, pos); } catch { /* ignore */ }
        setCaret(pos);
      }
    });
  };
  const dictation = useDictation({
    onTranscript: appendText,
    onError: (msg) => {
      console.warn('[ChatComposerV2] dictation error:', msg);
    },
    // TICKET-936: every transcript is kept until it is sent or discarded.
    safetyScope: 'chat',
  });
  const safety = useDictationSafetyNet({ input });

  const effectiveSlashText = manualSlashOpen && !input.startsWith('/') ? '/' : input;

  const slash = useSlashCommands({
    text: effectiveSlashText,
    caret: manualSlashOpen && !input.startsWith('/') ? 1 : caret,
  });
  const mention = useAtMentions({
    text: input,
    caret,
    enabled: !slash.open,
  });

  useEffect(() => {
    const draft = loadDraft(conversationId);
    if (draft && !input) setInput(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when
    // conversationId flips; `input` + `setInput` come from useChat and we only
    // want the draft hydrated on conversation switch, not on every keystroke.
  }, [conversationId]);

  useEffect(() => {
    saveDraft(conversationId, input);
  }, [input, conversationId]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(GROW_MAX, Math.max(GROW_MIN, el.scrollHeight));
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > GROW_MAX ? 'auto' : 'hidden';
  }, [input]);

  useEffect(() => {
    if (slash.open) return;
    if (manualSlashOpen) setManualSlashOpen(false);
  }, [slash.open, manualSlashOpen]);

  const remaining = MAX_CHARS - input.length;
  const counterStyleActive = useMemo(() => {
    if (input.length >= MAX_CHARS) return counterErrorStyle;
    if (input.length >= WARN_AT) return counterWarnStyle;
    return counterStyle;
  }, [input.length]);

  const canSend = !loading && input.trim().length > 0;

  const commitSlash = useCallback((cmd) => {
    if (!cmd) return;
    try { validateCommand(cmd.slug); } catch (err) {
      console.warn('[ChatComposerV2] invalid command:', err.message);
      return;
    }
    if (cmd.dispatch === 'local') {
      onLocalCommand?.(cmd.localHandler, cmd);
      setInput('');
      saveDraft(conversationId, '');
      setManualSlashOpen(false);
      return;
    }
    if (cmd.requiresScope) {
      const hasScope = Array.isArray(chat?.scope)
        && chat.scope.some((e) => e?.type === cmd.requiresScope);
      if (!hasScope) {
        onLocalCommand?.('assign-project', cmd);
        return;
      }
    }
    const text = `/${cmd.slug}`;
    setInput(text);
    setManualSlashOpen(false);
    queueMicrotask(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        const pos = text.length;
        try { el.setSelectionRange(pos, pos); } catch { /* ignore */ }
        setCaret(pos);
      }
    });
  }, [chat?.scope, conversationId, onLocalCommand, setInput]);

  const commitMention = useCallback((payload) => {
    if (!payload) return;
    setInput(payload.text);
    if (payload.entry && typeof chat?.addScopeEntry === 'function') {
      chat.addScopeEntry(payload.entry);
    }
    queueMicrotask(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        try { el.setSelectionRange(payload.caret, payload.caret); } catch { /* ignore */ }
        setCaret(payload.caret);
      }
    });
  }, [chat, setInput]);

  const handleChange = useCallback((e) => {
    const v = e.target.value ?? '';
    setInput(v.length > MAX_CHARS ? v.slice(0, MAX_CHARS) : v);
    setCaret(e.target.selectionStart ?? v.length);
  }, [setInput]);

  const handleKeyUp = useCallback((e) => {
    setCaret(e.target.selectionStart ?? 0);
  }, []);

  const handleClick = useCallback((e) => {
    setCaret(e.target.selectionStart ?? 0);
  }, []);

  const handleSend = useCallback(() => {
    if (!canSend) return;
    safety.settleSent(input);
    onSend?.(input);
    saveDraft(conversationId, '');
  }, [canSend, input, onSend, conversationId, safety]);

  const handleKeyDown = useCallback((e) => {
    if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
      e.preventDefault();
      setManualSlashOpen(true);
      return;
    }
    if (slash.open) {
      if (slash.onKeyDown(e, commitSlash, () => setManualSlashOpen(false))) return;
    } else if (mention.open) {
      if (mention.onKeyDown(e, commitMention, () => { /* close by typing/escaping */ })) return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [slash, mention, commitSlash, commitMention, handleSend]);

  const handleFileChange = useCallback((e) => {
    // TICKET-912 slice 4: every picked file (the host routes them through WikiLLM).
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    onFileSelect?.(files);
    e.target.value = '';
  }, [onFileSelect]);

  // TICKET-1528: Ctrl+V / Cmd+V of a screenshot routes it like a picked file.
  // Text-only pastes fall through to the textarea untouched.
  const handlePaste = useCallback((e) => {
    if (!onFileSelect) return;
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return;
    e.preventDefault();
    onFileSelect(files);
  }, [onFileSelect]);

  const openFilePicker = useCallback(() => {
    setAttachOpen(false);
    fileInputRef.current?.click();
  }, []);

  const slashMenuOpen = slash.open || manualSlashOpen;

  return (
    <div
      style={composerRootStyle}
      data-testid="chat-composer-v2"
      data-voice-mode={voiceMode ? 'on' : 'off'}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
        data-testid="chat-composer-file-input"
      />

      <SlashCommandMenu
        open={slashMenuOpen}
        results={slash.results}
        cursor={slash.cursor}
        onHover={slash.setCursor}
        onSelect={commitSlash}
      />
      <AtMentionPicker
        open={mention.open && !slashMenuOpen}
        results={mention.results}
        cursor={mention.cursor}
        onHover={mention.setCursor}
        onSelect={(hit) => {
          const payload = mention.buildInsertion(hit);
          if (payload) commitMention(payload);
        }}
      />
      <AttachMenu
        open={attachOpen}
        onSelectFile={openFilePicker}
        onSelectPhoto={openFilePicker}
        onSelectVoice={onVoiceMode ? () => { setAttachOpen(false); onVoiceMode(); } : undefined}
      />

      <div style={composerRowStyle}>
        <button
          type="button"
          onClick={() => setAttachOpen((o) => !o)}
          style={attachOpen ? iconBtnActiveStyle : iconBtnStyle}
          aria-label="Attach"
          aria-expanded={attachOpen}
          data-testid="chat-composer-attach"
        >
          <Plus size={20} strokeWidth={2} />
        </button>

        <div style={textareaWrapStyle}>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onPaste={handlePaste}
            onClick={handleClick}
            placeholder="Message Claude…"
            style={textareaStyle}
            rows={1}
            maxLength={MAX_CHARS}
            data-testid="chat-composer-textarea"
            aria-label="Message input"
          />
        </div>

        {/* Mic — one-click dictation (voice-to-text). Click once to start:
            the icon swaps for a live waveform so the user can see "I'm
            being heard." Click again to stop + transcribe + insert into
            the composer. No chunks, no pipeline, no overlay — just
            record-one-blob → Whisper → text. */}
        <button
          type="button"
          onClick={dictation.toggle}
          disabled={dictation.transcribing}
          style={dictation.recording || dictation.transcribing ? iconBtnActiveStyle : iconBtnStyle}
          aria-label={dictation.recording ? 'Stop dictation' : dictation.transcribing ? 'Transcribing' : 'Start dictation'}
          aria-pressed={dictation.recording}
          data-testid="chat-composer-dictate"
          title={dictation.error || (dictation.recording ? 'Click to stop and transcribe' : 'Click to dictate')}
        >
          {dictation.transcribing ? (
            <Loader2 size={20} strokeWidth={2} style={{ animation: 'spin 900ms linear infinite' }} />
          ) : dictation.recording ? (
            <DictationWaveform level={dictation.audioLevel} />
          ) : (
            <Mic size={20} strokeWidth={2} />
          )}
        </button>

        {/* Headphones — pop-out full voice conversation overlay.
            Separate from the mic button because dictation (write one
            message) and conversation (keep talking) are different modes. */}
        {/* TICKET-912: hidden when the host offers no voice mode (Quote Copilot until
            its voice slice) -- a button that does nothing is worse than none. */}
        {onVoiceMode && (
          <button
            type="button"
            onClick={onVoiceMode}
            style={voiceMode ? iconBtnActiveStyle : iconBtnStyle}
            aria-label="Voice mode"
            aria-pressed={voiceMode}
            data-testid="chat-composer-voice"
          >
            <Headphones size={20} strokeWidth={2} />
          </button>
        )}

        {loading ? (
          <button
            type="button"
            onClick={onStop}
            style={sendBtnStyle}
            aria-label="Stop generating"
            data-testid="chat-composer-stop"
          >
            <StopCircle size={20} strokeWidth={2} />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            style={canSend ? sendBtnStyle : sendBtnDisabledStyle}
            aria-label="Send message"
            data-testid="chat-composer-send"
          >
            <Send size={18} strokeWidth={2} />
          </button>
        )}
      </div>

      {/* TICKET-324 — a dictation that failed to transcribe is saved, not lost.
          This is the only place the user can see it and get it back. */}
      <DictationRecoveryBar dictation={dictation} />
      {/* TICKET-936 — dictated text that left the box without being sent. */}
      <DictationRestoreBar safety={safety} onRestore={appendText}
        busy={dictation.recording || dictation.transcribing} />

      {input.length >= WARN_AT && (
        <div style={metaRowStyle} data-testid="chat-composer-meta">
          <span />
          <span style={counterStyleActive} data-testid="chat-composer-counter">
            {input.length >= MAX_CHARS
              ? `Maximum length reached (${MAX_CHARS.toLocaleString()}) — extra text was not added`
              : `${remaining} left`}
          </span>
        </div>
      )}
    </div>
  );
}

export { COMMANDS as AVAILABLE_COMMANDS };
